// "Upcoming" — the Home rail of the next big competitions and pro cards:
// ADCC, IBJJF majors, WNO, Polaris, Fight to Win and the like. Read from
// stats/upcoming_events, which scheduledUpcomingEvents in functions/index.js
// refreshes daily from FloGrappling's public event feed. Each tile links out
// to the event's page (schedule, brackets, results) — we're a signpost, not
// a competition site.
//
// Hidden until there's a list: no events, no rail.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useGuest, useGuestStat } from '../guestMode';
import { CalendarDays } from 'lucide-react';
import { db } from '../firebase';
import Rail from './Rail';
import { TileRailSkeleton } from './RailSkeleton';

/** How many tiles the rail carries, matching Popular. */
const SHOWN = 10;

interface UpcomingEvent {
  id: number;
  /** Display name: the feed's title minus the year and sponsor tail. */
  name: string;
  /** Start date, YYYY-MM-DD. */
  start: string;
  city?: string;
  thumbnail?: string;
  url: string;
  /** The feed had no artwork for this one, just its generic logo. */
  placeholder: boolean;
}

/** The promotion behind an event, from its name, and the logo we keep for
 *  it in public/logos. One mark per organisation, the same every time,
 *  rather than whatever poster the feed happened to have. */
const PROMOTIONS: { test: RegExp; logo: string; label: string; /** A squarish mark that fills the band's height reads bigger than a wide one: shown smaller. */ compact?: boolean }[] = [
  { test: /ADCC/i, logo: '/logos/adcc.png', label: 'ADCC' },
  { test: /IBJJF/i, logo: '/logos/ibjjf.svg', label: 'IBJJF' },
  { test: /Polaris/i, logo: '/logos/polaris.png', label: 'Polaris' },
  { test: /Fight to Win|F2W/i, logo: '/logos/f2w.png', label: 'Fight 2 Win' },
  { test: /Finishers/i, logo: '/logos/finishers.png', label: 'Finishers', compact: true },
  { test: /Tap Cancer Out/i, logo: '/logos/tco.png', label: 'Tap Cancer Out' },
];
function promotionOf(name: string) {
  return PROMOTIONS.find((p) => p.test.test(name));
}

/** "2026-09-12" -> "Sep 12" — the date as written, no timezone shift. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function Upcoming({ feed }: {
  /** Render this many events into Home's four-across feed instead of the rail. */
  feed?: number;
}) {
  const { guest, stats } = useGuest();
  const guestEvents = useGuestStat<{ items?: UpcomingEvent[] }>('upcomingEvents');
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  // Whether the doc has answered yet — an empty list and a list still on its
  // way look the same in `events`, and only one of them gets a skeleton.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Guests read the public copy instead — stats/upcoming_events needs auth.
    if (guest) return;
    return onSnapshot(doc(db, 'stats', 'upcoming_events'), (snap) => {
      // Kept whole: the rail takes SHOWN of these, the feed goes further.
      setEvents((snap.data()?.items ?? []) as UpcomingEvent[]);
      setLoaded(true);
    }, () => { setLoaded(true); /* not signed in, or no list yet */ });
  }, [guest]);

  // A guest's copy rides the context, which is null until it has settled.
  if (guest ? !stats : !loaded) {
    // In the feed the tab is already the label, so there is nothing to hold
    // the space with; the grid just fills in when the list lands.
    return feed !== undefined ? null : <TileRailSkeleton title="Upcoming" icon={<CalendarDays size={16} />} />;
  }

  const all = guest ? (guestEvents?.items ?? []) : events;

  if (feed !== undefined) {
    const shown = all.slice(0, feed);
    if (shown.length === 0) {
      return <div className="home-feed-empty">Nothing on the calendar right now.</div>;
    }
    return (
      <div className="technique-grid home-grid">
        {shown.map((e) => <EventTile e={e} key={e.id} />)}
      </div>
    );
  }

  const shown = all.slice(0, SHOWN);
  if (shown.length === 0) return null;

  return (
    <Rail title="Upcoming" icon={<CalendarDays size={16} />} tiles>
      {shown.map((e) => (
        <div className="rail-cell" key={e.id}>
          <EventTile e={e} />
        </div>
      ))}
    </Rail>
  );
}

/** One event: the promotion's mark on the app's dark, the date and name at
 *  the foot, the whole tile a link out to the event's own page. */
function EventTile({ e }: { e: UpcomingEvent }) {
  const promo = promotionOf(e.name);
  return (
    <a
      className="technique-tile event-tile"
      href={e.url}
      target="_blank"
      rel="noopener noreferrer"
      title={e.city ? `${e.name} — ${e.city}` : e.name}
    >
      {/* The promotion's own mark on the app's dark, or the name alone
          for one we don't keep a logo for. */}
      {promo
        ? <img className={`event-tile-logo${promo.compact ? ' event-tile-logo--compact' : ''}`} src={promo.logo} alt={promo.label} loading="lazy" />
        : <span className="event-tile-nologo">{e.name.split(' ')[0]}</span>}
      {/* The date sits just above the name, both centred at the foot. */}
      <span className="event-tile-foot">
        <span className="rail-chip rail-chip--date">{shortDate(e.start)}</span>
        <span className="technique-tile-name"><span className="event-tile-name-text">{e.name}</span></span>
      </span>
    </a>
  );
}
